import { Injectable } from '@angular/core';
import {
  formFieldBoolean,
  formFieldDate,
  formFieldString,
  ObjectWorkflowIntegrationFormField,
  TypedFormField,
  TypedFormTable,
  WorkflowIntegrationResponse,
  WorkflowIntegrationStep,
} from '@cassiopeia/integration';
import { requiredValue, StringUtils } from '@nestech/utils';
import { SelfServiceWorkflowApi } from '../danish-pension';

export type TaxType = 'TaxFree' | 'Taxable';
export type PayoutType = 'annuity' | 'lifetimeAnnuity' | 'sum';

export interface CoverToDisburse {
  coverId: string;
  id: string;
  name: string;
  umr: string;
  depot: number;
  defaultBenefitType: PayoutType;
  taxType: TaxType;
  isPrivate?: boolean;
  chosen?: boolean;
  taxable?: string;
  benefit?: number;
  infoBoxKey: string;
  payoutDescription: string;
  taxTypeKey: string;
}

export type CoverDetails = Omit<CoverToDisburse, 'chosen'> & { cassiopeiaConfigSupported?: boolean };

export interface CoverStepWithDetails extends RetirementPensionStep {
  metaData: {
    isCoverConfig?: boolean;
    coverDetails: CoverDetails;
  };
}

export interface ValeurDateStep extends Omit<WorkflowIntegrationStep, 'formFields'> {
  formFields: {
    valeurDate: TypedFormField<Date>;
    sameValeurDateWanted: TypedFormField<boolean>;
    shouldSkipControl?: TypedFormField<boolean>;
  };
  metaData?: {
    infoKeys?: { key: string }[];
  };
}

export interface CoversToDisburse extends Omit<WorkflowIntegrationStep, 'formTables'> {
  formTables: WorkflowIntegrationStep['formTables'] & {
    coversToDisburse: TypedFormTable<CoverToDisburse>;
  };
  metaData: {
    useConvertToSum?: boolean;
  };
}

type PayoutAccountStep = WorkflowIntegrationStep & {
  formFields: {
    taxCardType: TypedFormField<string>;
  };
};

export interface RetirementPension extends WorkflowIntegrationResponse {
  valeurDate: ValeurDateStep;
  coversToDisburse: CoversToDisburse;
  coversToDisburseSteps: CoverStepWithDetails[];
  payoutAccount: PayoutAccountStep;
  payoutComplete: WorkflowIntegrationStep;
}

export type RetirementPensionStep = WorkflowIntegrationStep;
export type RetirementPensionFormField = ObjectWorkflowIntegrationFormField;

const toCoverStepWithDetails = (c: WorkflowIntegrationStep): CoverStepWithDetails => {
  const details = requiredValue<CoverDetails>(c.metaData?.['coverDetails']?.[0], 'coverDetails');
  return {
    ...c,
    metaData: {
      ...c.metaData,
      coverDetails: {
        ...details,
        coverId: requiredValue<string>(details.coverId, 'coverId'),
        id: requiredValue<string>(details.id, 'id'),
        umr: requiredValue<string>(details.umr, 'umr'),
        depot: requiredValue<number>(details.depot, 'depot'),
        taxType: details.taxable ? 'Taxable' : 'TaxFree',
        defaultBenefitType: requiredValue<PayoutType>(details.defaultBenefitType, 'defaultBenefitType'),
        ...getMessageKeysForCover(details),
      },
    },
  };
};

const getMessageKeysForCover = (cover: CoverDetails) => {
  const infoBoxKeyPostFix = cover.infoBoxKey ? `-${cover.infoBoxKey}` : cover.umr;

  return {
    name: 'cas.wizardRetirementPensionCoverName-' + cover.coverId,
    payoutDescription: `cas.payoutType${StringUtils.capitalizeFirstLetter(cover.defaultBenefitType ?? '')}${StringUtils.capitalizeFirstLetter(cover.taxable ?? '')}`,
    infoBoxKey: 'cas.payoutInfo' + infoBoxKeyPostFix,
    taxTypeKey: `cas.taxType-${cover.taxable?.toLocaleLowerCase()}`,
  } as Partial<CoverToDisburse>;
};

const mapApiToRetirementPension = (api: WorkflowIntegrationResponse): RetirementPension => {
  const valeurDateStepOrig = api.steps?.['valeurDate'];
  const coversToDisburseStep = api.steps?.['coversToDisburse'] as CoversToDisburse;
  const payoutAccountStep = api.steps?.['payoutAccount'];
  const payoutCompleteStep = api.steps?.['payoutComplete'];
  const coversToDisburseSteps = Object.values(api.steps || {}).filter(s => s.metaData?.['isCoverConfig'] === true);

  if (!valeurDateStepOrig) throw new Error('valeurDate step is required');
  if (!coversToDisburseStep) throw new Error('coversToDisburse step is required');
  if (!payoutAccountStep) throw new Error('payoutAccountStep step is required');
  if (!payoutCompleteStep) throw new Error('payoutCompleteStep step is required');

  return {
    ...api,
    payoutAccount: {
      ...payoutAccountStep,
      formFields: {
        ...payoutAccountStep.formFields,
        taxCardType: formFieldString('taxCardType', payoutAccountStep.formFields),
      },
    },
    valeurDate: {
      ...valeurDateStepOrig,
      formFields: {
        ...valeurDateStepOrig.formFields,
        valeurDate: formFieldDate('valeurDate', valeurDateStepOrig.formFields),
        sameValeurDateWanted: formFieldBoolean('sameValeurDateWanted', valeurDateStepOrig.formFields),
      },
    },
    coversToDisburse: {
      ...coversToDisburseStep,
      formTables: {
        ...coversToDisburseStep.formTables,
        coversToDisburse: {
          ...coversToDisburseStep.formTables?.['coversToDisburse'],
          tableData:
            coversToDisburseStep.formTables?.['coversToDisburse']?.tableData?.map(td => {
              const coverToDisburse = td as CoverToDisburse;
              const taxType = coverToDisburse.taxable === 'taxable' ? 'Taxable' : 'TaxFree';
              return {
                ...coverToDisburse,
                taxType,
                chosen: !!td['chosen'],
                ...getMessageKeysForCover(coverToDisburse),
              };
            }) || [],
        },
      },
      metaData: {
        ...coversToDisburseStep.metaData,
        useConvertToSum: coversToDisburseStep.metaData?.['useConvertToSum'],
      },
    },
    coversToDisburseSteps: coversToDisburseSteps?.map(toCoverStepWithDetails) || [],
    payoutComplete: payoutCompleteStep,
  };
};

@Injectable()
export class RetirementPensionApi extends SelfServiceWorkflowApi<RetirementPension> {
  override mapToModel(response: WorkflowIntegrationResponse): RetirementPension {
    return mapApiToRetirementPension(response);
  }

  override actionName = 'retirementPension';
}
